import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Send, X, Download, FolderTree, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageTransition } from "@/components/PageTransition";
import { BottomNavigation } from "@/components/BottomNavigation";
import { MenuDrawer } from "@/components/MenuDrawer";
import WallpaperCategoryManager from "@/components/WallpaperCategoryManager";
import SfondiPro from "@/components/SfondiPro";
import { useAdminRole } from "@/hooks/useAdminRole";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Import existing wallpaper images
import appleSilver from "@/assets/wallpapers/apple-silver.jpg";
import appleSpaceBlack from "@/assets/wallpapers/apple-space-black.jpg";
import appleGold from "@/assets/wallpapers/apple-gold.jpg";
import applePacificBlue from "@/assets/wallpapers/apple-pacific-blue.jpg";
import appleDeepPurple from "@/assets/wallpapers/apple-deep-purple.jpg";
import applePink from "@/assets/wallpapers/apple-pink.jpg";
import appleGreen from "@/assets/wallpapers/apple-green.jpg";
import appleOrange from "@/assets/wallpapers/apple-orange.jpg";
import appleRed from "@/assets/wallpapers/apple-red.jpg";
import appleSierraBlue from "@/assets/wallpapers/apple-sierra-blue.jpg";
import appleMidnight from "@/assets/wallpapers/apple-midnight.jpg";
import appleTitanium from "@/assets/wallpapers/apple-titanium.jpg";
import appleCyan from "@/assets/wallpapers/apple-cyan.jpg";
import appleYellow from "@/assets/wallpapers/apple-yellow.jpg";
import appleSonoma from "@/assets/wallpapers/apple-sonoma.jpg";
import appleSequoia from "@/assets/wallpapers/apple-sequoia.jpg";
import appleDynamic from "@/assets/wallpapers/apple-dynamic.jpg";

// Import new Apple wallpapers
import appleIndigo from "@/assets/wallpapers/apple-indigo.jpg";
import appleRoseGold from "@/assets/wallpapers/apple-rose-gold.jpg";
import appleAqua from "@/assets/wallpapers/apple-aqua.jpg";
import appleAurora from "@/assets/wallpapers/apple-aurora.jpg";
import appleCoral from "@/assets/wallpapers/apple-coral.jpg";
import appleEmerald from "@/assets/wallpapers/apple-emerald.jpg";
import appleBronze from "@/assets/wallpapers/apple-bronze.jpg";
import appleLavender from "@/assets/wallpapers/apple-lavender.jpg";
import appleRuby from "@/assets/wallpapers/apple-ruby.jpg";
import appleIce from "@/assets/wallpapers/apple-ice.jpg";
// 10 NEW Apple wallpapers
import appleOcean from "@/assets/wallpapers/apple-ocean.jpg";
import appleSunset from "@/assets/wallpapers/apple-sunset.jpg";
import appleNorthern from "@/assets/wallpapers/apple-northern.jpg";
import appleNebula from "@/assets/wallpapers/apple-nebula.jpg";
import appleDesert from "@/assets/wallpapers/apple-desert.jpg";
import appleTropical from "@/assets/wallpapers/apple-tropical.jpg";
import appleCrystal from "@/assets/wallpapers/apple-crystal.jpg";
import appleVolcano from "@/assets/wallpapers/apple-volcano.jpg";
import appleForest from "@/assets/wallpapers/apple-forest.jpg";
import appleGalaxy from "@/assets/wallpapers/apple-galaxy.jpg";
// 5 NEW Apple wallpapers (2nd batch)
import appleMercury from "@/assets/wallpapers/apple-mercury.jpg";
import appleElectric from "@/assets/wallpapers/apple-electric.jpg";
import appleHolographic from "@/assets/wallpapers/apple-holographic.jpg";
import appleMint from "@/assets/wallpapers/apple-mint.jpg";
import appleChampagne from "@/assets/wallpapers/apple-champagne.jpg";

// Import existing car wallpapers
import carFerrari from "@/assets/wallpapers/car-ferrari.jpg";
import carLamborghini from "@/assets/wallpapers/car-lamborghini.jpg";
import carPorsche from "@/assets/wallpapers/car-porsche.jpg";

// Import new car wallpapers
import carMclaren from "@/assets/wallpapers/car-mclaren.jpg";
import carBugatti from "@/assets/wallpapers/car-bugatti.jpg";
import carAstonMartin from "@/assets/wallpapers/car-aston-martin.jpg";
import carMercedesAmg from "@/assets/wallpapers/car-mercedes-amg.jpg";
import carBmwM8 from "@/assets/wallpapers/car-bmw-m8.jpg";
import carAudiRs from "@/assets/wallpapers/car-audi-rs.jpg";
import carRollsRoyce from "@/assets/wallpapers/car-rolls-royce.jpg";
// 10 NEW car wallpapers
import carMaserati from "@/assets/wallpapers/car-maserati.jpg";
import carKoenigsegg from "@/assets/wallpapers/car-koenigsegg.jpg";
import carPagani from "@/assets/wallpapers/car-pagani.jpg";
import carLexusLfa from "@/assets/wallpapers/car-lexus-lfa.jpg";
import carNissanGtr from "@/assets/wallpapers/car-nissan-gtr.jpg";
import carToyotaSupra from "@/assets/wallpapers/car-toyota-supra.jpg";
import carCorvette from "@/assets/wallpapers/car-corvette.jpg";
import carFordGt from "@/assets/wallpapers/car-ford-gt.jpg";
import carAlfaRomeo from "@/assets/wallpapers/car-alfa-romeo.jpg";
import carBentley from "@/assets/wallpapers/car-bentley.jpg";
// 5 NEW car wallpapers (2nd batch)
import carPorscheGt3 from "@/assets/wallpapers/car-porsche-gt3.jpg";
import carFerrariSf90 from "@/assets/wallpapers/car-ferrari-sf90.jpg";
import carLamborghiniRevuelto from "@/assets/wallpapers/car-lamborghini-revuelto.jpg";
import carMclaren750s from "@/assets/wallpapers/car-mclaren-750s.jpg";
import carAstonDb12 from "@/assets/wallpapers/car-aston-db12.jpg";

// Import F1 wallpapers (18)
import f1RedBull from "@/assets/wallpapers/f1-red-bull.jpg";
import f1Ferrari from "@/assets/wallpapers/f1-ferrari.jpg";
import f1Mercedes from "@/assets/wallpapers/f1-mercedes.jpg";
import f1Mclaren from "@/assets/wallpapers/f1-mclaren.jpg";
import f1AstonMartin from "@/assets/wallpapers/f1-aston-martin.jpg";
import f1Alpine from "@/assets/wallpapers/f1-alpine.jpg";
import f1Williams from "@/assets/wallpapers/f1-williams.jpg";
import f1Haas from "@/assets/wallpapers/f1-haas.jpg";
// 5 NEW F1 wallpapers
import f1Helmet from "@/assets/wallpapers/f1-helmet.jpg";
import f1Pitstop from "@/assets/wallpapers/f1-pitstop.jpg";
// 5 NEW F1 race-specific wallpapers
import f1RedbullMonaco from "@/assets/wallpapers/f1-redbull-monaco.jpg";
import f1FerrariMonza from "@/assets/wallpapers/f1-ferrari-monza.jpg";
import f1MercedesSilverstone from "@/assets/wallpapers/f1-mercedes-silverstone.jpg";
import f1MclarenSingapore from "@/assets/wallpapers/f1-mclaren-singapore.jpg";
import f1AstonAbudhabi from "@/assets/wallpapers/f1-aston-abudhabi.jpg";

// Import Calcio wallpapers (20)
import calcioBall from "@/assets/wallpapers/calcio-ball.jpg";
import calcioStadium from "@/assets/wallpapers/calcio-stadium.jpg";
import calcioGoal from "@/assets/wallpapers/calcio-goal.jpg";
import calcioBoots from "@/assets/wallpapers/calcio-boots.jpg";
import calcioTrophy from "@/assets/wallpapers/calcio-trophy.jpg";
import calcioBallonDor from "@/assets/wallpapers/calcio-ballon-dor.jpg";
import calcioCorner from "@/assets/wallpapers/calcio-corner.jpg";
import calcioReferee from "@/assets/wallpapers/calcio-referee.jpg";
import calcioGloves from "@/assets/wallpapers/calcio-gloves.jpg";
import calcioWorldCup from "@/assets/wallpapers/calcio-world-cup.jpg";
// 5 NEW Calcio wallpapers
import calcioCelebration from "@/assets/wallpapers/calcio-celebration.jpg";
import calcioBicycleKick from "@/assets/wallpapers/calcio-bicycle-kick.jpg";
import calcioSave from "@/assets/wallpapers/calcio-save.jpg";
import calcioFreekick from "@/assets/wallpapers/calcio-freekick.jpg";
import calcioDribbling from "@/assets/wallpapers/calcio-dribbling.jpg";
// 5 NEW Calcio player/team wallpapers
import calcioRonaldo from "@/assets/wallpapers/calcio-ronaldo.jpg";
import calcioMessi from "@/assets/wallpapers/calcio-messi.jpg";
import calcioRealMadrid from "@/assets/wallpapers/calcio-real-madrid.jpg";
import calcioManCity from "@/assets/wallpapers/calcio-man-city.jpg";
import calcioMbappe from "@/assets/wallpapers/calcio-mbappe.jpg";

// Wallpaper categories type for filtering
type WallpaperCategorySlug = "all" | "apple" | "cars" | "f1" | "calcio" | string;

interface DBCategory {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
}

interface Wallpaper {
  id: number;
  image: string;
  name: string;
  category: "apple" | "cars" | "f1" | "calcio";
}

// All wallpapers - 42 Apple + 25 Cars + 18 F1 + 20 Calcio = 105 total
const allWallpapers: Wallpaper[] = [
  // Apple wallpapers (42)
  { id: 1, image: appleDynamic, name: "Dynamic Island", category: "apple" },
  { id: 2, image: appleSonoma, name: "Sonoma", category: "apple" },
  { id: 3, image: appleSequoia, name: "Sequoia", category: "apple" },
  { id: 4, image: appleSilver, name: "Silver", category: "apple" },
  { id: 5, image: appleSpaceBlack, name: "Space Black", category: "apple" },
  { id: 6, image: appleGold, name: "Gold", category: "apple" },
  { id: 7, image: applePacificBlue, name: "Pacific Blue", category: "apple" },
  { id: 8, image: appleDeepPurple, name: "Deep Purple", category: "apple" },
  { id: 9, image: applePink, name: "Pink", category: "apple" },
  { id: 10, image: appleGreen, name: "Green", category: "apple" },
  { id: 11, image: appleOrange, name: "Orange", category: "apple" },
  { id: 12, image: appleRed, name: "Product Red", category: "apple" },
  { id: 13, image: appleSierraBlue, name: "Sierra Blue", category: "apple" },
  { id: 14, image: appleMidnight, name: "Midnight", category: "apple" },
  { id: 15, image: appleTitanium, name: "Titanium", category: "apple" },
  { id: 16, image: appleCyan, name: "Cyan", category: "apple" },
  { id: 17, image: appleYellow, name: "Yellow", category: "apple" },
  { id: 18, image: appleIndigo, name: "Indigo", category: "apple" },
  { id: 19, image: appleRoseGold, name: "Rose Gold", category: "apple" },
  { id: 20, image: appleAqua, name: "Aqua", category: "apple" },
  { id: 21, image: appleAurora, name: "Aurora", category: "apple" },
  { id: 22, image: appleCoral, name: "Coral", category: "apple" },
  { id: 23, image: appleEmerald, name: "Emerald", category: "apple" },
  { id: 24, image: appleBronze, name: "Bronze", category: "apple" },
  { id: 25, image: appleLavender, name: "Lavender", category: "apple" },
  { id: 26, image: appleRuby, name: "Ruby", category: "apple" },
  { id: 27, image: appleIce, name: "Ice", category: "apple" },
  { id: 28, image: appleOcean, name: "Ocean", category: "apple" },
  { id: 29, image: appleSunset, name: "Sunset", category: "apple" },
  { id: 30, image: appleNorthern, name: "Northern Lights", category: "apple" },
  { id: 31, image: appleNebula, name: "Nebula", category: "apple" },
  { id: 32, image: appleDesert, name: "Desert", category: "apple" },
  { id: 33, image: appleTropical, name: "Tropical", category: "apple" },
  { id: 34, image: appleCrystal, name: "Crystal", category: "apple" },
  { id: 35, image: appleVolcano, name: "Volcano", category: "apple" },
  { id: 36, image: appleForest, name: "Forest", category: "apple" },
  { id: 37, image: appleGalaxy, name: "Galaxy", category: "apple" },
  { id: 38, image: appleMercury, name: "Mercury", category: "apple" },
  { id: 39, image: appleElectric, name: "Electric", category: "apple" },
  { id: 40, image: appleHolographic, name: "Holographic", category: "apple" },
  { id: 41, image: appleMint, name: "Mint Aurora", category: "apple" },
  { id: 42, image: appleChampagne, name: "Champagne", category: "apple" },
  // Car wallpapers (25)
  { id: 43, image: carFerrari, name: "Ferrari", category: "cars" },
  { id: 44, image: carLamborghini, name: "Lamborghini", category: "cars" },
  { id: 45, image: carPorsche, name: "Porsche", category: "cars" },
  { id: 46, image: carMclaren, name: "McLaren", category: "cars" },
  { id: 47, image: carBugatti, name: "Bugatti", category: "cars" },
  { id: 48, image: carAstonMartin, name: "Aston Martin", category: "cars" },
  { id: 49, image: carMercedesAmg, name: "Mercedes AMG", category: "cars" },
  { id: 50, image: carBmwM8, name: "BMW M8", category: "cars" },
  { id: 51, image: carAudiRs, name: "Audi RS", category: "cars" },
  { id: 52, image: carRollsRoyce, name: "Rolls Royce", category: "cars" },
  { id: 53, image: carMaserati, name: "Maserati", category: "cars" },
  { id: 54, image: carKoenigsegg, name: "Koenigsegg", category: "cars" },
  { id: 55, image: carPagani, name: "Pagani", category: "cars" },
  { id: 56, image: carLexusLfa, name: "Lexus LFA", category: "cars" },
  { id: 57, image: carNissanGtr, name: "Nissan GT-R", category: "cars" },
  { id: 58, image: carToyotaSupra, name: "Toyota Supra", category: "cars" },
  { id: 59, image: carCorvette, name: "Corvette", category: "cars" },
  { id: 60, image: carFordGt, name: "Ford GT", category: "cars" },
  { id: 61, image: carAlfaRomeo, name: "Alfa Romeo", category: "cars" },
  { id: 62, image: carBentley, name: "Bentley", category: "cars" },
  { id: 63, image: carPorscheGt3, name: "Porsche 911 GT3 RS", category: "cars" },
  { id: 64, image: carFerrariSf90, name: "Ferrari SF90", category: "cars" },
  { id: 65, image: carLamborghiniRevuelto, name: "Lamborghini Revuelto", category: "cars" },
  { id: 66, image: carMclaren750s, name: "McLaren 750S", category: "cars" },
  { id: 67, image: carAstonDb12, name: "Aston Martin DB12", category: "cars" },
  // F1 wallpapers (18)
  { id: 68, image: f1RedBull, name: "F1 Red Bull", category: "f1" },
  { id: 69, image: f1Ferrari, name: "F1 Ferrari", category: "f1" },
  { id: 70, image: f1Mercedes, name: "F1 Mercedes", category: "f1" },
  { id: 71, image: f1Mclaren, name: "F1 McLaren", category: "f1" },
  { id: 72, image: f1AstonMartin, name: "F1 Aston Martin", category: "f1" },
  { id: 73, image: f1Alpine, name: "F1 Alpine", category: "f1" },
  { id: 74, image: f1Williams, name: "F1 Williams", category: "f1" },
  { id: 75, image: f1Haas, name: "F1 Haas", category: "f1" },
  { id: 76, image: f1Helmet, name: "F1 Helmet", category: "f1" },
  { id: 77, image: f1Pitstop, name: "F1 Pit Stop", category: "f1" },
  { id: 78, image: f1RedbullMonaco, name: "Red Bull Monaco GP", category: "f1" },
  { id: 79, image: f1FerrariMonza, name: "Ferrari Monza GP", category: "f1" },
  { id: 80, image: f1MercedesSilverstone, name: "Mercedes Silverstone", category: "f1" },
  { id: 81, image: f1MclarenSingapore, name: "McLaren Singapore", category: "f1" },
  { id: 82, image: f1AstonAbudhabi, name: "Aston Martin Abu Dhabi", category: "f1" },
  // Calcio wallpapers (20)
  { id: 83, image: calcioBall, name: "Pallone", category: "calcio" },
  { id: 84, image: calcioStadium, name: "Stadio", category: "calcio" },
  { id: 85, image: calcioGoal, name: "Rete", category: "calcio" },
  { id: 86, image: calcioBoots, name: "Scarpe", category: "calcio" },
  { id: 87, image: calcioTrophy, name: "Champions League", category: "calcio" },
  { id: 88, image: calcioBallonDor, name: "Pallone d'Oro", category: "calcio" },
  { id: 89, image: calcioCorner, name: "Calcio d'Angolo", category: "calcio" },
  { id: 90, image: calcioReferee, name: "Arbitro", category: "calcio" },
  { id: 91, image: calcioGloves, name: "Guanti Portiere", category: "calcio" },
  { id: 92, image: calcioWorldCup, name: "Coppa del Mondo", category: "calcio" },
  { id: 93, image: calcioCelebration, name: "Esultanza Gol", category: "calcio" },
  { id: 94, image: calcioBicycleKick, name: "Rovesciata", category: "calcio" },
  { id: 95, image: calcioSave, name: "Parata", category: "calcio" },
  { id: 96, image: calcioFreekick, name: "Punizione", category: "calcio" },
  { id: 97, image: calcioDribbling, name: "Dribbling", category: "calcio" },
  { id: 98, image: calcioRonaldo, name: "Ronaldo SIUUU", category: "calcio" },
  { id: 99, image: calcioMessi, name: "Messi Inter Miami", category: "calcio" },
  { id: 100, image: calcioRealMadrid, name: "Real Madrid Champions", category: "calcio" },
  { id: 101, image: calcioManCity, name: "Manchester City", category: "calcio" },
  { id: 102, image: calcioMbappe, name: "Mbappé Real Madrid", category: "calcio" },
];

const Sfondi = () => {
  const navigate = useNavigate();
  const [selectedWallpaper, setSelectedWallpaper] = useState<Wallpaper | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<WallpaperCategorySlug>("all");
  const [dbCategories, setDbCategories] = useState<DBCategory[]>([]);
  const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);
  const [isSfondiProOpen, setIsSfondiProOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [hiddenWallpaperIds, setHiddenWallpaperIds] = useState<number[]>([]);
  
  const { isAdmin } = useAdminRole(userId);

  // Fetch user and categories from database
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserId(data.user?.id || null);
    });
    
    fetchCategories();
    fetchHiddenWallpapers();
  }, []);

  const fetchCategories = async () => {
    const { data } = await supabase
      .from("wallpaper_categories")
      .select("*")
      .order("sort_order", { ascending: true });
    
    if (data) setDbCategories(data);
  };

  const fetchHiddenWallpapers = async () => {
    const { data } = await supabase
      .from("hidden_wallpapers")
      .select("wallpaper_id");
    
    if (data) {
      setHiddenWallpaperIds(data.map(h => h.wallpaper_id));
    }
  };

  // Filter wallpapers based on selected category AND hidden status
  const visibleWallpapers = allWallpapers.filter(w => !hiddenWallpaperIds.includes(w.id));
  const filteredWallpapers = selectedCategory === "all" 
    ? visibleWallpapers 
    : visibleWallpapers.filter(w => w.category === selectedCategory);

  // Map db categories to display format
  const categories = dbCategories.map(cat => ({
    id: cat.slug as WallpaperCategorySlug,
    label: cat.name
  }));

  const handleShare = async () => {
    if (!selectedWallpaper) return;

    try {
      // Fetch the image and convert to blob
      const response = await fetch(selectedWallpaper.image);
      const blob = await response.blob();
      
      if (navigator.canShare && navigator.canShare({ files: [new File([blob], "sfondo-tecly.jpg", { type: "image/jpeg" })] })) {
        const file = new File([blob], `sfondo-tecly-${selectedWallpaper.name.toLowerCase().replace(/\s+/g, '-')}.jpg`, { type: "image/jpeg" });
        await navigator.share({
          title: `Sfondo Tecly - ${selectedWallpaper.name}`,
          text: `Guarda questo bellissimo sfondo Tecly - ${selectedWallpaper.name}!`,
          files: [file],
        });
        return;
      }
    } catch (error) {
      console.log("File sharing failed, trying link sharing", error);
    }

    // Fallback to link sharing
    const shareData: ShareData = {
      title: `Sfondo Tecly - ${selectedWallpaper.name}`,
      text: `Guarda questo bellissimo sfondo Tecly - ${selectedWallpaper.name}!`,
      url: `${window.location.origin}/sfondi?id=${selectedWallpaper.id}`,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.log("Condivisione annullata", error);
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(shareData.url ?? "");
        toast.success("Link copiato negli appunti");
      } catch {
        toast.error("Impossibile condividere questo sfondo");
      }
    } else {
      toast.error("Condivisione non supportata");
    }
  };

  const handleSave = async () => {
    if (!selectedWallpaper) return;
    
    try {
      // Fetch the image
      const response = await fetch(selectedWallpaper.image);
      const blob = await response.blob();
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = `sfondo-tecly-${selectedWallpaper.name.toLowerCase().replace(/\s+/g, '-')}.jpg`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success("Sfondo salvato!");
    } catch (error) {
      console.error("Error saving wallpaper:", error);
      toast.error("Errore nel salvataggio");
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
          <div className="flex items-center gap-4 px-4 py-4">
            <motion.button
              onClick={() => navigate("/")}
              className="p-2 rounded-full hover:bg-muted transition-colors"
              whileHover={{ scale: 1.1, rotate: -8 }}
              whileTap={{ scale: 0.9, rotate: 0 }}
            >
              <ArrowLeft className="w-6 h-6" />
            </motion.button>
            <h1 className="text-xl font-bold flex-1">Sfondi</h1>
            {isAdmin && (
              <motion.button
                onClick={() => setIsCategoryManagerOpen(true)}
                className="p-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="Gestione Categorie"
              >
                <FolderTree className="w-5 h-5" />
              </motion.button>
            )}
          </div>
        </div>

        {/* Category Filter with Blue Animation */}
        <div className="px-4 pt-4">
          <div className="flex gap-2 items-center overflow-x-auto scrollbar-hide pb-1">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex-shrink-0 ${
                  selectedCategory === category.id
                    ? "bg-blue-500 text-white shadow-lg shadow-blue-500/30"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                animate={selectedCategory === category.id ? { 
                  scale: [1, 1.02, 1],
                  transition: { duration: 0.3 }
                } : {}}
                layout
              >
                {category.label}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Sfondi Pro Card */}
        <div className="px-4 pt-4">
          <motion.div
            onClick={() => setIsSfondiProOpen(true)}
            className="w-full p-4 rounded-2xl bg-gradient-to-r from-yellow-500/20 via-amber-500/20 to-yellow-600/20 border border-yellow-500/30 cursor-pointer"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center shadow-lg shadow-yellow-500/30">
                <Crown className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-foreground">Sfondi Pro</h3>
                <p className="text-sm text-muted-foreground">Collezione esclusiva di sfondi</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Wallpapers Grid */}
        <div className="px-4 py-6">
          <div className="grid grid-cols-2 gap-4 pb-24">
            {filteredWallpapers.map((wallpaper, index) => (
              <motion.div
                key={wallpaper.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="w-full aspect-[9/16] rounded-2xl overflow-hidden cursor-pointer shadow-lg"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedWallpaper(wallpaper)}
                layout
              >
                <img 
                  src={wallpaper.image} 
                  alt={wallpaper.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Wallpaper Preview Fullscreen */}
        <AnimatePresence>
          {selectedWallpaper && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[200]"
              data-tecly-wallpaper-preview="true"
            >
              {/* Fullscreen Wallpaper */}
              <img 
                src={selectedWallpaper.image}
                alt={selectedWallpaper.name}
                className="w-full h-full object-cover"
              />
              
              {/* iOS-style translucent controls */}
              <div className="absolute top-0 left-0 right-0 z-10 preview-controls">
                <div className="flex items-center justify-between px-4 py-4">
                  {/* Close Button */}
                  <motion.button
                    onClick={() => setSelectedWallpaper(null)}
                    className="p-3 rounded-full bg-black/20 backdrop-blur-xl border border-white/10"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X className="w-6 h-6 text-white drop-shadow-lg" />
                  </motion.button>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-3">
                    {/* Delete Button - Admin Only */}
                    
                    {/* Save Button */}
                    <motion.button
                      onClick={handleSave}
                      className="p-3 rounded-full bg-black/20 backdrop-blur-xl border border-white/10"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Download className="w-6 h-6 text-white drop-shadow-lg" />
                    </motion.button>
                    
                    {/* Share Button */}
                    <motion.button
                      onClick={handleShare}
                      className="p-3 rounded-full bg-black/20 backdrop-blur-xl border border-white/10"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Send className="w-6 h-6 text-white drop-shadow-lg" />
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>


        {/* Bottom Navigation */}
        {userId && (
          <>
            <BottomNavigation onMenuClick={() => setIsMenuOpen(true)} />
            <MenuDrawer open={isMenuOpen} onOpenChange={setIsMenuOpen} />
          </>
        )}

        {/* Wallpaper Category Manager for Admins */}
        {isAdmin && (
          <WallpaperCategoryManager
            isOpen={isCategoryManagerOpen}
            onClose={() => {
              setIsCategoryManagerOpen(false);
              fetchCategories(); // Refresh categories after changes
            }}
          />
        )}

        {/* Sfondi Pro Modal */}
        <SfondiPro
          isOpen={isSfondiProOpen}
          onClose={() => setIsSfondiProOpen(false)}
        />
      </div>
    </PageTransition>
  );
};

export default Sfondi;
